<template>
	<Navigation />

	<ClientOnly>
		<Transition>
			<ContentSidebar v-if="store.contentSidebar.show" />
		</Transition>
	</ClientOnly>

	<slot />

	<Footer />

	<ClientOnly>
		<PromoModalAd />
	</ClientOnly>
</template>

<script setup>
import { useStocksStore } from "../stores/stocks";
import { organizationSchema, websiteSchema } from "~/utils/organization-schema";
const $config = useRuntimeConfig();
const { gtm_id, env } = $config.public;

const $route = useRoute();
const { pageKey } = usePageKey();
const contentIsReady = ref(false);
const stock_filters = ref([]);

const stocksStore = useStocksStore();

const setDesktopCtaText = (newPath) => {
	// let payload;
	// if (newPath === "news" || newPath === "news-slug") {
	// 	payload = {
	// 		title: "Stay Ahead Of The News - Sign Up For My Weekly Newsletter",
	// 		buttonText: "Get My Watchlist Here",
	// 		link:
	// 			"https://timsykeswatchlist.com/organic/?utm_source=blog&utm_medium=ed_mention&utm_campaign=rep0s6"
	// 	};
	// } else {
	const payload = {
		title: "Join Thousands Profiting From Smart Trades!",
		buttonText: "TRADE LIKE TIM",
		link: withUtmContent(
			"https://www.timothysykes.com/millionaire-challenge/?utm_source=blog&utm_medium=popup&utm_campaign=Qhdzor",
			pageKey.value
		)
	};
	// }

	store.setDesktopCtaMessage(payload);
};

watch(
	() => $route.name,
	(newPath, oldPath) => {
		if (process.client) {
			if (newPath !== oldPath) {
				setDesktopCtaText(newPath);
			}

			if (newPath !== "blog-slug" && newPath !== "widgetpage") {
				store.toggleContentSidebarVisibility(false);
				store.setContentSidebarId(null);
				store.setContentSidebarData(null);
			}
		}
	}
);

// Generate head script for top level tracking parameters (equivalent to PHP generateHeadScripts)
// Using the exact page ID from the working configuration

useHead(() => {
	const head_obj = {
		bodyAttrs: {
			class: "full-width-content"
		},
		link: [
			{ rel: "preconnect", href: "https://fonts.googleapis.com" },
			{
				rel: "preconnect",
				href: "https://fonts.gstatic.com",
				crossorigin: ""
			},
			{ rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
			{ rel: "manifest", href: "/site.webmanifest" },
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "/fav-16x16.png"
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/fav-32x32.png"
			},
			{ rel: "mask-icon", color: "#5BBAD5", href: "/safari-pinned-tab.svg" },
			{ rel: "icon", type: "apple-touch-icon", href: "/apple-touch-icon.png" }
		],
		meta: [
			{ name: "msapplication-TileColor", content: "#DA532C" },
			{ name: "theme-color", content: "#FFFFFF" },
			getRobotsMeta(env)
		],
		script: [{ src: "/js/prebid.js" }]
	};

	if (env === "production") {
		head_obj.script = [
			{ src: "/js/prebid.js" },
			{
				async: true,
				src: `https://www.googletagmanager.com/gtm.js?id=${gtm_id}`,
				body: true
			}
		];
	}

	// OneSignal Custom Code install. Scripts are first-party under /js so
	// Firefox ETP / ad blockers that block cdn.onesignal.com still load the SDK.
	head_obj.script.push(
		{
			key: "onesignal-sdk",
			src: "/js/OneSignalSDK.page.js",
			defer: true,
			tagPriority: 10
		},
		{
			key: "onesignal-init",
			tagPriority: 999,
			innerHTML: `window.OneSignalDeferred = window.OneSignalDeferred || [];
  OneSignalDeferred.push(async function(OneSignal) {
    await OneSignal.init({
      appId: "7951cf12-4d25-4cbb-affc-6dc0d4fe89e6",
      safari_web_id: "web.onesignal.auto.0b3c1e09-f01e-4f75-a6ff-3f857f927766",
      notifyButton: {
        enable: true,
      },
      promptOptions: {
        slidedown: {
          prompts: [{
            type: "push",
            autoPrompt: true,
            text: {
              actionMessage: "Subscribe to receive notifications",
              acceptButton: "Allow",
              cancelButton: "No, thanks"
            }
          }]
        }
      }
    });
    // v16 Bell.ts returns without rendering unless CSS.supports('anchor-name').
    // Safari (and older browsers) fail that check, so use the slidedown instead.
    if (!CSS.supports("anchor-name: --a")) {
      await OneSignal.Slidedown.promptPush();
    }
  });`
		},
		{
			key: "schema-organization",
			type: "application/ld+json",
			innerHTML: JSON.stringify(organizationSchema)
		},
		{
			key: "schema-website",
			type: "application/ld+json",
			innerHTML: JSON.stringify(websiteSchema)
		}
	);

	return head_obj;
});

const loadAccessibe = () => {
	const s = document.createElement("script");
	const h = document.querySelector("head") || document.body;
	s.src = "https://acsbapp.com/apps/app/dist/js/app.js";
	s.async = true;
	s.onload = function () {
		acsbJS.init({
			statementLink: "",
			footerHtml: "",
			hideMobile: false,
			hideTrigger: false,
			// Opt-in only: don't auto-force focus rings on click/keyboard remediations
			disableBgProcess: true,
			language: "en",
			position: "right",
			leadColor: "#146FF8",
			triggerColor: "#146FF8",
			triggerRadius: "50%",
			triggerPositionX: "right",
			triggerPositionY: "bottom",
			triggerIcon: "people",
			triggerSize: "bottom",
			triggerOffsetX: 20,
			triggerOffsetY: 20,
			mobile: {
				triggerSize: "small",
				triggerPositionX: "right",
				triggerPositionY: "bottom",
				triggerOffsetX: 10,
				triggerOffsetY: 10,
				triggerRadius: "20"
			}
		});
	};
	h.appendChild(s);
};

onMounted(async () => {
	if (process.client) {
		stock_filters.value.push("most_active");

		setDesktopCtaText($route.name);

		if (env === "production") {
			loadAccessibe();
		}
	}
});
</script>

<style lang="scss">
@import "@assets/utils.scss";

// OneSignal notify bell / slide-down (initialized via the Custom Code
// snippet in this layout's `useHead`).
@media (max-width: 900px) {
	#onesignal-slidedown-container.slide-up {
		top: 0 !important;
		bottom: auto !important;
	}
}

// Keep the bell on-screen (negative `right` clipped it on mobile) and above
// AccessiBe + the sticky CTA.


#onesignal-bell-container {
	bottom: 88px !important;
	display: block !important;
	right: 8px !important;
	transform: scale(1.2) !important;
	transform-origin: bottom right !important;
	position: fixed;
}

@media (min-width: 900px) {
	#onesignal-bell-container {
		bottom: 40px !important;
		display: block !important;
		position: fixed;
		right: 8px !important;
		transform: scale(1.5) !important;
		transform-origin: bottom right !important;
	}
}
.swiper-slide p {
	font-size: 15.2px;
}

.swiper-slide-button.prev {
	left: -36px;
	top: -52px;
}

.swiper-slide-button.next {
	right: -36px;
	top: -52px;
}

.testimonial-text .testimonial-author {
	font-size: 20px;
}

.testimonial-text h5 {
	font-size: 10.5px;
}

@media (min-width: 768px) {
	.swiper-slide p {
		font-size: 19px;
	}

	.swiper-slide-button.prev {
		left: 0;
		top: -32px;
	}

	.swiper-slide-button.next {
		right: 0;
		top: -32px;
	}
}

.tablepress {
	border-collapse: collapse;
	border-spacing: 0;
	width: 100%;
	border: 1px solid $neutral-200;
	margin-bottom: 32px;
	overflow: auto;
	display: block;
	@media (min-width: 768px) {
		display: table;
	}
	tr:nth-child(2n + 1) {
		background-color: #fff;
	}

	th {
		padding-top: 11px;
		padding-bottom: 11px;
		background-color: $primary-500;
		color: white;
	}

	td,
	th {
		text-align: left;
		padding: 12px 8px;
		white-space: nowrap;
	}
}

@keyframes pulse-red {
	0% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7);
	}

	70% {
		transform: scale(1);
		box-shadow: 0 0 0 10px transparent;
	}

	100% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 transparent;
	}
}

.blob.red {
	background: #ff5252;
	box-shadow: 0 0 0 0 #ff5252;
	animation: pulse-red 2s infinite;
	margin-left: 0;
	margin-right: 12px;
	@include breakpoint(lg) {
		margin-left: 12px;
		margin-right: 8px;
	}
}

.blob {
	background: #000;
	border-radius: 50%;
	box-shadow: 0 0 0 0 #000;
	margin: 14px 8px 15px 12px;
	height: 10px;
	width: 10px;
	transform: scale(1);
	animation: pulse-black 2s infinite;
}

.nav-link {
	color: #fff;
	font-family: Fira Sans;
	font-size: 18px;
	font-style: normal;
	height: 38px;
	font-weight: 400;
	line-height: 40px;
	/* 36px */
	letter-spacing: 0.44px;
	position: relative;
	padding: 0 16px;
	visibility: visible;
	display: block;
	@include breakpoint(lg) {
		padding: 0 12px;
	}
	&.live {
		border-radius: 8px;
		background: $error-700;
		display: flex;
		padding: 6px 8px;
		align-items: center;
		font-weight: 600;
		gap: 8px;
		line-height: 1;
		font-size: 12px;
		color: white;

		&:hover {
			background: $error-500;
		}

		@include breakpoint(lg) {
			padding: 6px 12px 6px 0;
			font-size: 15px;
			height: 38px;
			gap: 0;
		}
	}
}

.ad-buttler-ribbon-outer-wrapper {
	background-color: #ffeb3b;
	z-index: 10;
}

.ad-buttler-ribbon-outer-wrapper p {
	color: #222;
}

.author-body {
	a:not(.author-publications) {
		font-size: 18px;
		display: revert-layer;
		align-items: center;
		position: relative;
		color: $primary-500;
		line-height: normal;
		&::after {
			content: "";
			background-image: url("/images/icons/external-link.svg");
			width: 14px;
			height: 14px;
			display: inline-block;
			background-size: contain;
			margin-left: 2px;
		}

		&:hover {
			background-color: #eab308;
			color: #1e3a8a;
			text-decoration: underline;
			&::after {
				background-image: url("/images/icons/external-link-hover.svg");
			}
		}
	}
}

.author-title a {
	color: #93c5fd;
}

.author-side-box {
	grid-column: 3 / span 1;
	grid-row: 1 / span 2;
	width: 100%;
	border-radius: 24px;
	background-color: #fff;
	display: flex;
	flex-direction: column;
	padding: 20px;
	gap: 20px;
	box-shadow: 0 7px 20px 0 rgba(0, 0, 0, 0.10196078431372549);
	@include breakpoint(md) {
		width: 100%;
		max-width: 380px;
	}

	@include breakpoint(lg) {
		position: sticky;
		top: 92px;
	}
}

.author-watchlist-subscription-ad {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.author-watchlist-image {
	width: 100%;
}

.author-watchlist-image img {
	width: 100%;
	height: auto;
	-o-object-fit: cover;
	object-fit: cover;
	border-radius: 4px;
}

.author-watchlist-information-title {
	padding-top: 24px;
	padding-bottom: 0;
	font-size: 24px;
	font-weight: 700;
	line-height: 29px;
	letter-spacing: -0.5px;
	text-align: left;

	@include breakpoint(lg) {
		font-size: 20px;
	}
	@include breakpoint(xxl) {
		font-size: 26px;
	}
}

.author-watchlist-information-subtitle {
	padding-top: 8px;
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: 0;
	text-align: center;
	color: #1e293b;
	width: 100%;
	display: block;
	margin-bottom: 12px;
}

.author-watchlist-information-email-input::placeholder {
	color: #878788;
	text-align: center;
}

.author-watchlist-information-email-input {
	width: 100%;
	margin-bottom: 8px;
	padding: 10px;
	border: 1px solid #cbd5e1;
	border-radius: 4px;
}

.author-watchlist-information-subscribe-button {
	background-color: #ef6820;
	color: #fff;
	font-weight: 700;
	border: none;
	border-radius: 8px;
	padding: 6px 10px;
	font-size: 16px;
	cursor: pointer;
	position: relative;
	transition: background-color 0.3s ease;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 12px;

	.arrow {
		line-height: 1;
		font-size: 32px;
		height: 32px;
	}
}

.author-watchlist-information-description {
	margin-top: 8px;
	display: flex;
	justify-content: center;
	align-items: center;
	font-family: Rubik;
	font-size: 12px;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: 0;
	text-align: center;
	color: #1e293b;
}

.header-watch-live a.nav-link.live {
	height: 40px !important;
	padding: 6px 8px !important;
	font-size: 11px !important;
	letter-spacing: -0.25px !important;
}
.header-watch-live .blob {
	margin-left: 0 !important;
}
.header-watch-live .flex.items-center.justify-center {
	margin-left: 0 !important;
}
</style>
